import 'react-big-calendar/lib/css/react-big-calendar.css';
"use client";

import { useState, useEffect } from 'react';
import { PlusCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Appointment, Doctor, Patient } from "@/types";
import { supabase } from "@/utils/supabase/client";
import { Calendar as BigCalendar, momentLocalizer, Views, NavigateAction, View } from 'react-big-calendar';
import moment from 'moment';
import { useToast } from "@/components/ui/use-toast";
import { AppointmentFormModal } from '@/components/appointment-form-modal';
import { useAuth } from '@/contexts/auth-context';

const localizer = momentLocalizer(moment);

export default function CalendarPage() {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const { toast } = useToast();
  const { currentOrganization } = useAuth();

  useEffect(() => {
    if (currentOrganization) {
      fetchAppointments();
      fetchDoctors();
      fetchPatients();
    }
  }, [currentOrganization]);

  const fetchAppointments = async () => {
    if (!currentOrganization) return;
    try {
      const { data, error } = await supabase
        .from('appointments')
        .select('*, patients(*), doctors(*)')
        .eq('organization_id', currentOrganization.id);
      if (error) throw error;
      setAppointments(data || []);
    } catch (error: any) {
      toast({
        title: "Error fetching appointments",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const fetchDoctors = async () => {
    if (!currentOrganization) return;
    try {
      const { data, error } = await supabase
        .from('doctors')
        .select('*')
        .eq('organization_id', currentOrganization.id);
      if (error) throw error;
      setDoctors(data || []);
    } catch (error: any) {
      toast({
        title: "Error fetching doctors",
        description: error.message,
        variant: "destructive",
      });
    }
  };
  
  const fetchPatients = async () => {
    if (!currentOrganization) return;
    try {
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .eq('organization_id', currentOrganization.id);
      if (error) throw error;
      setPatients(data || []);
    } catch (error: any) {
      toast({
        title: "Error fetching patients",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleSelectSlot = (slotInfo: { start: Date; end: Date }) => {
    setSelectedAppointment({
      id: '',
      created_at: '',
      appointment_date: slotInfo.start.toISOString(),
      appointment_time: '',
      status: 'Scheduled',
      patient_id: '',
      doctor_id: '',
      appointment_type: '',
      patients: null,
      doctors: null,
      organization_id: currentOrganization?.id
    } as unknown as Appointment);
    setIsModalOpen(true);
  };

  const handleSelectEvent = (event: any) => {
    setSelectedAppointment(event.resource);
    setIsModalOpen(true);
  };

  const handleSaveAppointment = () => {
    fetchAppointments();
    setIsModalOpen(false);
    setSelectedAppointment(null);
  };

  const events = appointments.map(appt => ({
    title: `${appt.patients?.name || 'Patient'} with ${appt.doctors?.name || 'Doctor'}`,
    start: new Date(appt.appointment_date),
    end: new Date(new Date(appt.appointment_date).getTime() + 30 * 60 * 1000), // Assuming 30-min appt
    resource: appt,
  }));

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Calendar</h1>
        <Button onClick={() => handleSelectSlot({ start: new Date(), end: new Date() })}>
          <PlusCircle className="mr-2 h-4 w-4" />
          Create Appointment
        </Button>
      </div>
      <div className="h-[75vh]">
        {/* @ts-ignore */}
        <BigCalendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: '100%' }}
          onSelectSlot={handleSelectSlot}
          onSelectEvent={handleSelectEvent}
          selectable
        />
      </div>
      <AppointmentFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveAppointment}
        appointmentToEdit={selectedAppointment}
        doctors={doctors}
        patients={patients}
      />
    </div>
  );
}